/**
 * El emparejamiento de la tablet con la caseta.
 *
 * ## Por qué existe
 *
 * El servidor de un condominio vive en una IP de la red local. Sin dominio no
 * hay autoridad que avale su certificado, así que el sistema lo rechaza y la
 * app se queda sin poder hablar por HTTPS. La salida fácil —dejar todo en HTTP
 * plano— pone la contraseña del guardia al alcance de cualquiera conectado al
 * wifi del fraccionamiento.
 *
 * En vez de eso, la tablet reconoce **un certificado concreto**: durante la
 * instalación se le enseña su huella al técnico, él la compara con la que
 * imprime el servidor, y a partir de ahí esa tablet solo habla con ese
 * certificado. Es lo mismo que hace SSH la primera vez que entras a una
 * máquina, y traslada la confianza al único momento en que se puede verificar
 * de verdad: una persona mirando las dos pantallas.
 *
 * ## Qué hay de este lado
 *
 * Nada que decida. La comparación de huellas que protege la conexión ocurre en
 * el lado nativo, en cada petición, sin pasar por aquí (ver `ClienteAnclado`).
 * Este módulo solo sirve para **enseñar** la huella y para guardar el «sí» de
 * la persona. En el navegador no hay complemento nativo y todo esto se apaga
 * solo: ahí el certificado lo avala una autoridad como en cualquier web.
 */

import { esAppNativa } from './servidor';

export interface Certificado {
  /** SHA-256 en pares separados por dos puntos, como lo imprime el servidor. */
  huella: string;
  sujeto: string;
  hasta: string;
  anfitrion: string;
}

interface PluginEmparejamiento {
  mirar(opciones: { url: string }): Promise<Certificado>;
  anclar(opciones: { url: string; huella: string }): Promise<void>;
  anclada(opciones: { url: string }): Promise<{ huella: string | null }>;
  olvidar(opciones: { url: string }): Promise<void>;
}

function plugin(): PluginEmparejamiento | null {
  const cap = (globalThis as { Capacitor?: { Plugins?: Record<string, unknown> } }).Capacitor;
  return (cap?.Plugins?.Emparejamiento as PluginEmparejamiento | undefined) ?? null;
}

/**
 * Si esta dirección necesita emparejarse.
 *
 * Solo el HTTPS del APK. Una dirección `http://` no tiene certificado que
 * comparar —y por eso mismo no protege nada, pero se sigue permitiendo
 * mientras existan demostraciones y aparatos de reja que no hablan cifrado—.
 */
export function requiereEmparejar(url: string): boolean {
  return esAppNativa() && url.startsWith('https://') && plugin() !== null;
}

/** Qué certificado sirve esa dirección. Mirar no es confiar: no ancla nada. */
export function mirarCertificado(url: string): Promise<Certificado> {
  const p = plugin();
  if (!p) throw new Error('Esta versión no sabe emparejarse.');
  return p.mirar({ url });
}

export async function huellaAnclada(url: string): Promise<string | null> {
  const p = plugin();
  if (!p) return null;
  return (await p.anclada({ url })).huella;
}

/** La persona comparó la huella y dijo que sí. */
export async function anclar(url: string, huella: string): Promise<void> {
  await plugin()?.anclar({ url, huella });
}

export async function olvidarAncla(url: string): Promise<void> {
  await plugin()?.olvidar({ url });
}

/**
 * La huella partida en grupos para poder compararla de un vistazo.
 *
 * Sesenta y cuatro caracteres seguidos no se comparan: se miran los primeros
 * cuatro y se da por bueno el resto. En bloques cortos el ojo sí recorre la
 * línea entera, y ese recorrido es literalmente lo único que sostiene todo
 * este esquema.
 */
export function enGrupos(huella: string, porFila = 8): string[] {
  const pares = huella.split(':');
  const filas: string[] = [];
  for (let i = 0; i < pares.length; i += porFila) {
    filas.push(pares.slice(i, i + porFila).join(':'));
  }
  return filas;
}

/** Lo que viene dentro del QR de la etiqueta del equipo. */
export interface Etiqueta {
  /**
   * Con dos puntos, como se compara y como se enseña.
   *
   * **Puede no venir**, y no es un descuido. Un código puede llevar solo la
   * dirección: es lo que se usa con un servidor cuyo certificado avala una
   * autoridad de verdad —detrás de un túnel, o el día que haya dominio—. Ahí no
   * hay huella que anclar, y anclarla sería peor que no hacerlo, porque esos
   * certificados se renuevan solos y la tablet se quedaría fuera.
   */
  huella?: string;
  /** La dirección. Es una pista, no la identidad. */
  servidor?: string;
}

export class EtiquetaInvalida extends Error {}

/**
 * Interpreta lo que la cámara acaba de leer.
 *
 * Estricto a propósito. Esto decide con qué servidor va a hablar la tablet para
 * el resto de su vida, así que cualquier cosa que no sea exactamente una
 * etiqueta de Kodica se rechaza en vez de intentar adivinar. Un QR de un
 * tarjetón, del wifi del condominio o de un refresco tiene que dar un «esto no
 * es la etiqueta», no un error críptico tres pantallas después.
 *
 * La huella se acepta con o sin los dos puntos: la etiqueta los quita para que
 * el código salga más pequeño y se lea mejor, pero alguien podría teclear la
 * forma larga.
 */
export function leerEtiqueta(texto: string): Etiqueta {
  let carga: unknown;
  try {
    carga = JSON.parse(texto);
  } catch {
    throw new EtiquetaInvalida('Ese código no es la etiqueta de una caseta.');
  }

  if (typeof carga !== 'object' || carga === null) {
    throw new EtiquetaInvalida('Ese código no es la etiqueta de una caseta.');
  }
  const datos = carga as Record<string, unknown>;

  if (datos.kodica !== 1) {
    throw new EtiquetaInvalida('Ese código no es la etiqueta de una caseta.');
  }

  const servidor =
    typeof datos.servidor === 'string' && datos.servidor ? datos.servidor : undefined;

  // Sin huella se acepta, **pero entonces la dirección es obligatoria**: un
  // código que no trae ninguna de las dos no dice nada y lo honesto es
  // rechazarlo, en vez de dejar al técnico frente a una pantalla que no cambió.
  if (datos.huella === undefined) {
    if (!servidor) {
      throw new EtiquetaInvalida('Ese código no trae ni dirección ni huella.');
    }
    return { servidor };
  }

  const crudo =
    typeof datos.huella === 'string' ? datos.huella.replace(/:/g, '').toUpperCase() : '';
  if (!/^[0-9A-F]{64}$/.test(crudo)) {
    throw new EtiquetaInvalida('La etiqueta está dañada: la huella no se entiende.');
  }

  const huella = (crudo.match(/../g) ?? []).join(':');
  return servidor ? { huella, servidor } : { huella };
}
